// Minimal "raise a ticket" dialog — agent-side. Picks a portal + request
// type, captures title/description/priority, and posts to the API.
// Phase 2 will extend this with full custom form-field rendering for
// public requesters; for Phase 1 we only honour the schema if the
// selected request type defines fields, otherwise it's a free-form title.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dot, PRIORITY_DOT, PRIORITY_OPTIONS, priorityLabel } from './ticketMeta';
import { toast } from 'sonner';

export default function CreateTicketDialog({ open, onClose, portals = [], defaultPortalId = null, onCreated }) {
  const { API, getOrgHeaders } = useAuth();
  const [portalId, setPortalId] = useState(defaultPortalId || portals[0]?.portal_id || '');
  const [requestTypeId, setRequestTypeId] = useState('');
  const [requestTypes, setRequestTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPortalId(defaultPortalId || portals[0]?.portal_id || '');
      setRequestTypeId('');
      setTitle('');
      setDescription('');
      setPriority('medium');
    }
  }, [open, defaultPortalId, portals]);

  useEffect(() => {
    if (!open || !portalId) { setRequestTypes([]); setGroups([]); return; }
    setGroupId('');
    setRequestTypeId('');
    axios.get(`${API}/service-desk/portals/${portalId}/request-types`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setRequestTypes(r.data || []))
      .catch(() => setRequestTypes([]));
    axios.get(`${API}/service-desk/portals/${portalId}/groups`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setGroups(r.data || []))
      .catch(() => setGroups([]));
  }, [open, portalId, API, getOrgHeaders]);

  const portal = useMemo(() => portals.find(p => p.portal_id === portalId), [portals, portalId]);

  // Group → request types. When a portal has groups, the agent picks a group
  // first and only that group's request types (plus any ungrouped ones via
  // "Ungrouped") are offered.
  const hasGroups = groups.length > 0;
  const ungroupedExists = useMemo(() => requestTypes.some(rt => !rt.portal_group_id), [requestTypes]);
  const visibleRTs = useMemo(() => {
    if (!hasGroups) return requestTypes;
    if (!groupId) return [];
    if (groupId === '__none__') return requestTypes.filter(rt => !rt.portal_group_id);
    return requestTypes.filter(rt => rt.portal_group_id === groupId);
  }, [hasGroups, groupId, requestTypes]);

  const submit = async () => {
    if (!portalId) return toast.error('Pick a portal');
    if (!title.trim()) return toast.error('Title is required');
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/service-desk/portals/${portalId}/tickets`,
        { title: title.trim(), description, priority, request_type_id: requestTypeId || null },
        { withCredentials: true, headers: getOrgHeaders() }
      );
      toast.success(`Ticket ${data.ticket_key} created`);
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create ticket');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="sd-create-ticket-dialog">
        <DialogHeader>
          <DialogTitle>New ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Portal</Label>
            <Select value={portalId} onValueChange={setPortalId}>
              <SelectTrigger data-testid="sd-ticket-portal-select"><SelectValue placeholder="Pick a portal" /></SelectTrigger>
              <SelectContent>
                {portals.map(p => (
                  <SelectItem key={p.portal_id} value={p.portal_id}>{p.key} · {p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {hasGroups && (
            <div>
              <Label className="text-xs">Group</Label>
              <Select value={groupId} onValueChange={(v) => { setGroupId(v); setRequestTypeId(''); }}>
                <SelectTrigger data-testid="sd-ticket-group-select"><SelectValue placeholder="Pick a group" /></SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
                  ))}
                  {ungroupedExists && <SelectItem value="__none__">Ungrouped</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          )}
          {(!hasGroups ? requestTypes.length > 0 : !!groupId) && (
            <div>
              <Label className="text-xs">Request type {hasGroups ? '' : '(optional)'}</Label>
              <Select value={requestTypeId} onValueChange={setRequestTypeId}>
                <SelectTrigger data-testid="sd-ticket-rt-select">
                  <SelectValue placeholder={visibleRTs.length ? 'General request' : 'No request types in this group'} />
                </SelectTrigger>
                <SelectContent>
                  {visibleRTs.map(rt => (
                    <SelectItem key={rt.request_type_id} value={rt.request_type_id}>{rt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Briefly describe the request" data-testid="sd-ticket-title-input" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Add any relevant details, links, or screenshots…" data-testid="sd-ticket-desc-input" />
          </div>
          <div>
            <Label className="text-xs">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="sd-ticket-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map(p => (
                  <SelectItem key={p} value={p}><span className="flex items-center gap-2"><Dot className={PRIORITY_DOT[p]} />{priorityLabel(p)}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !portal} data-testid="sd-ticket-submit">
            {submitting ? 'Creating…' : 'Create ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
